<template>
  <div>
    <KTCodePreview v-bind:title="'Hoa hồng của tôi'">
      <template v-slot:preview>
        <!-- Bộ lọc kỳ -->
        <b-row class="mb-5">
          <b-col md="2">
            <label class="form-label">Từ ngày</label>
            <date-picker
              placeholder="Từ ngày"
              class="form-control form-control-sm"
              :config="dpConfigs.date"
              v-model="filter.fromDate"
            ></date-picker>
          </b-col>
          <b-col md="2">
            <label class="form-label">Đến ngày</label>
            <date-picker
              placeholder="Đến ngày"
              class="form-control form-control-sm"
              :config="dpConfigs.date"
              v-model="filter.toDate"
            ></date-picker>
          </b-col>
          <b-col md="8" class="d-flex align-items-end flex-wrap">
            <b-button
              variant="primary"
              size="sm"
              class="mr-2 mt-2"
              :disabled="onLoading"
              @click="fetchData"
            >
              <i class="fas fa-search mr-1"></i>Xem
            </b-button>
            <b-button
              variant="light"
              size="sm"
              class="mr-2 mt-2"
              :disabled="onLoading"
              @click="selectMonth(0)"
              >Tháng này</b-button
            >
            <b-button
              variant="light"
              size="sm"
              class="mt-2"
              :disabled="onLoading"
              @click="selectMonth(1)"
              >Tháng trước</b-button
            >
          </b-col>
        </b-row>

        <vcl-table v-if="onLoading" :columns="6"></vcl-table>

        <div v-else-if="report">
          <div class="text-muted mb-4">
            <strong class="text-dark">{{ report.employee.fullName }}</strong>
            · {{ report.employee.jobTitleName }}
            <span v-if="report.employee.storeName">
              · {{ report.employee.storeName }}</span
            >
            · Kỳ {{ report.fromDate }} — {{ report.toDate }}
          </div>

          <!-- Thẻ tổng quan -->
          <b-row class="mb-2">
            <b-col lg="3" md="6" class="mb-3">
              <div class="stat-card stat-card--primary">
                <div class="stat-card__label">Tổng thực nhận</div>
                <div
                  class="stat-card__value stat-card__value--lg"
                  :class="amountClass(summary.finalAmount)"
                >
                  {{ displayAmount(summary.finalAmount) }}
                </div>
                <div class="stat-card__note">
                  {{ displayQuantity(summary.billCount) }} hoá đơn
                </div>
              </div>
            </b-col>
            <b-col lg="2" md="6" class="mb-3">
              <div class="stat-card">
                <div class="stat-card__label">Bán hàng</div>
                <div
                  class="stat-card__value"
                  :class="amountTone(summary.saleAmount)"
                >
                  {{ withSign(summary.saleAmount) }}
                </div>
                <div class="stat-card__note">
                  {{ displayQuantity(summary.saleQuantity) }} sản phẩm
                </div>
              </div>
            </b-col>
            <b-col lg="2" md="4" class="mb-3">
              <div class="stat-card">
                <div class="stat-card__label">Trả hàng</div>
                <div
                  class="stat-card__value"
                  :class="amountTone(-summary.refundAmount)"
                >
                  {{ withSign(-summary.refundAmount) }}
                </div>
                <div class="stat-card__note">
                  {{ displayQuantity(summary.refundQuantity) }} sản phẩm
                </div>
              </div>
            </b-col>
            <b-col lg="2" md="4" class="mb-3">
              <div class="stat-card">
                <div class="stat-card__label">Giao hàng · Thu cũ · AdHoc</div>
                <div class="stat-card__value" :class="amountTone(otherAmount)">
                  {{ withSign(otherAmount) }}
                </div>
                <div class="stat-card__note">
                  Giao {{ withSign(summary.shippingAmount) }} · Thu cũ
                  {{ withSign(summary.tradeInAmount) }} · AdHoc
                  {{ withSign(summary.adhocAmount) }}
                </div>
              </div>
            </b-col>
            <b-col lg="3" md="4" class="mb-3">
              <div class="stat-card">
                <div class="stat-card__label">Cộng / Trừ khác</div>
                <div class="stat-card__value">
                  <span :class="amountTone(summary.bonusAmount)">{{
                    withSign(summary.bonusAmount)
                  }}</span>
                  <span class="ml-2" :class="amountTone(-summary.minusAmount)">{{
                    withSign(-summary.minusAmount)
                  }}</span>
                </div>
                <div class="stat-card__note">
                  {{ report.adjustments.length }} khoản điều chỉnh
                </div>
              </div>
            </b-col>
          </b-row>

          <!-- Vai trò: một người có thể hưởng ở nhiều vai trò -->
          <div v-if="hasManyRoles" class="mb-4">
            <span class="section-label mr-2">Theo vai trò:</span>
            <b-button
              v-for="role in report.roles"
              :key="'role-' + String(role.jobTitleId)"
              size="sm"
              class="mr-2 mb-2"
              :variant="
                tableFilter.jobTitleId === role.jobTitleId
                  ? 'primary'
                  : 'outline-secondary'
              "
              @click="toggleFilter('jobTitleId', role.jobTitleId)"
            >
              {{ role.jobTitleName }}
              <span v-if="role.isMain">(chính)</span>:
              <strong>{{ displayAmount(role.amount) }}</strong>
            </b-button>
          </div>

          <!-- Nhóm hoa hồng -->
          <div v-if="report.categories.length" class="mb-5">
            <div class="section-label mb-2">
              Theo nhóm hoa hồng (bấm để lọc bảng bên dưới):
            </div>
            <button
              v-for="category in visibleCategories"
              :key="'cat-' + String(category.id)"
              type="button"
              class="bar-row"
              :class="{
                'bar-row--active': tableFilter.categoryId === category.id,
              }"
              :aria-pressed="String(tableFilter.categoryId === category.id)"
              @click="toggleFilter('categoryId', category.id)"
            >
              <span class="bar-row__name" :title="category.name">
                {{ category.name }}
              </span>
              <span class="bar-row__track">
                <span
                  class="bar-row__fill"
                  :class="{ 'bar-row__fill--minus': category.amount < 0 }"
                  :style="{ width: barWidth(category.amount) }"
                ></span>
              </span>
              <span class="bar-row__count">
                {{ displayQuantity(category.quantity) }} SP
              </span>
              <span class="bar-row__amount" :class="amountClass(category.amount)">
                {{ displayAmount(category.amount) }}
              </span>
            </button>
            <b-button
              v-if="report.categories.length > TOP_CATEGORY_COUNT"
              variant="link"
              size="sm"
              class="p-0 mt-1"
              @click="showAllCategories = !showAllCategories"
            >
              {{
                showAllCategories
                  ? 'Thu gọn'
                  : `Xem tất cả ${report.categories.length} nhóm`
              }}
            </b-button>
          </div>

          <!-- Bảng toàn bộ dòng hoa hồng -->
          <div class="section-label mb-2">Chi tiết từng hoá đơn:</div>
          <b-row class="mb-3">
            <b-col md="4" class="mb-2">
              <b-input
                size="sm"
                v-model="keywordInput"
                placeholder="Tìm số hoá đơn / sản phẩm"
                autocomplete="off"
                @input="debounceKeyword"
              ></b-input>
            </b-col>
            <b-col md="3" class="mb-2">
              <b-form-select
                size="sm"
                v-model="tableFilter.categoryId"
                :options="categoryOptions"
                @change="currentPage = 1"
              ></b-form-select>
            </b-col>
            <b-col md="5" class="mb-2">
              <b-form-radio-group
                v-model="tableFilter.kind"
                :options="kindOptions"
                buttons
                button-variant="outline-primary"
                size="sm"
                @change="currentPage = 1"
              ></b-form-radio-group>
            </b-col>
          </b-row>

          <b-table
            class="table-bordered table-hover"
            :fields="rowFields"
            :items="filteredRows"
            :per-page="PAGE_SIZE"
            :current-page="currentPage"
            primary-key="key"
            small
            responsive
            show-empty
            :empty-text="
              report.rows.length
                ? 'Không có dòng nào khớp bộ lọc'
                : 'Kỳ này chưa phát sinh hoa hồng'
            "
          >
            <template v-slot:cell(billDate)="row">
              {{ displayDate(row.item.billDate) }}
            </template>
            <template v-slot:cell(billNumber)="row">
              <div>{{ row.item.billNumber || '--' }}</div>
              <small v-if="row.item.relatedBillNumber" class="text-muted">
                Trả cho {{ row.item.relatedBillNumber }}
              </small>
            </template>
            <template v-slot:cell(kindName)="row">
              <b-badge :variant="kindVariant(row.item.kind)">
                {{ row.item.kindName }}
              </b-badge>
            </template>
            <template v-slot:cell(productName)="row">
              <div>{{ row.item.productName || '--' }}</div>
              <small class="text-muted">
                {{ row.item.categoryName }}
                <span v-if="row.item.conditionName">
                  · {{ row.item.conditionName }}</span
                >
              </small>
            </template>
            <template v-slot:cell(quantity)="row">
              <span :class="amountClass(row.item.quantity)">
                {{ row.item.quantity }}
              </span>
            </template>
            <template v-slot:cell(amount)="row">
              <span :class="amountClass(row.item.amount)">
                {{ displayAmount(row.item.amount) }}
              </span>
            </template>
          </b-table>

          <b-row>
            <b-col md="6">
              <p class="mt-2 text-dark">
                {{ displayQuantity(filteredRows.length) }} dòng · Tổng:
                <strong :class="amountClass(filteredTotal)">
                  {{ displayAmount(filteredTotal) }}
                </strong>
              </p>
            </b-col>
            <b-col md="6">
              <b-pagination
                v-if="filteredRows.length > PAGE_SIZE"
                v-model="currentPage"
                :total-rows="filteredRows.length"
                :per-page="PAGE_SIZE"
                size="sm"
                align="right"
              ></b-pagination>
            </b-col>
          </b-row>

          <!-- Cộng / trừ khác -->
          <div v-if="report.adjustments.length" class="mt-4">
            <div class="section-label mb-2">Cộng / Trừ khác:</div>
            <b-table
              class="table-bordered"
              :fields="adjustFields"
              :items="report.adjustments"
              small
            >
              <template v-slot:cell(amount)="row">
                <span
                  :class="row.item.amount < 0 ? 'text-danger' : 'text-success'"
                >
                  {{ row.item.amount > 0 ? '+' : ''
                  }}{{ displayAmount(row.item.amount) }}
                </span>
              </template>
            </b-table>
          </div>
        </div>
      </template>
    </KTCodePreview>
  </div>
</template>

<script>
/**
 * HOA HỒNG CỦA TÔI (bản mới) — chỉ chạy engine V2.
 *
 * Một lần gọi GET /commission-reports/my lấy đủ tổng quan + toàn bộ dòng hoa hồng, nên
 * lọc / tìm / phân trang làm ngay trên trình duyệt, không gọi lại API.
 * Không truyền employeeId: BE lấy từ token, trang luôn là hoa hồng của người đăng nhập.
 *
 * App mobile vẫn dùng API cũ rewardReport/mobile — không liên quan trang này.
 */
import KTCodePreview from '@/view/content/CodePreview.vue';
import datePicker from 'vue-bootstrap-datetimepicker';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import ApiService from '@/core/services/api.service';
import moment from 'moment';
import { VclTable } from 'vue-content-loading';
import decounce from 'debounce';
import { TIME_TRIGGER } from '@/utils/constants';
import { makeToastFaile } from '@/utils/common';
import commissionReportMixin, {
  DATE_INPUT_FORMAT,
  thStyle,
} from './commissionReport.mixin';

const PAGE_SIZE = 20;
const TOP_CATEGORY_COUNT = 8;
const ALL_KINDS = 'all';
// "Không lọc" — không dùng null vì nhóm AdHoc / vai trò có thể mang id null
const NO_FILTER = 'all';

const emptyTableFilter = () => ({
  keyword: '',
  categoryId: NO_FILTER,
  jobTitleId: NO_FILTER,
  kind: ALL_KINDS,
});

const KIND_VARIANT = Object.freeze({
  sale: 'success',
  refund: 'danger',
  shipping: 'primary',
  tradeIn: 'info',
  adhoc: 'warning',
});

export default {
  components: { KTCodePreview, VclTable, datePicker },
  mixins: [commissionReportMixin],
  data() {
    return {
      PAGE_SIZE,
      TOP_CATEGORY_COUNT,
      onLoading: false,
      report: null,
      filter: {
        fromDate: moment().startOf('month').format(DATE_INPUT_FORMAT),
        toDate: moment().format(DATE_INPUT_FORMAT),
      },
      tableFilter: emptyTableFilter(),
      keywordInput: '',
      currentPage: 1,
      showAllCategories: false,
      adjustFields: [
        { key: 'date', label: 'Ngày', thStyle: thStyle('15%') },
        { key: 'description', label: 'Nội dung', thStyle: thStyle('65%') },
        {
          key: 'amount',
          label: 'Số tiền',
          thStyle: thStyle('20%'),
          tdClass: 'text-right',
        },
      ],
    };
  },
  computed: {
    summary() {
      return this.report ? this.report.summary : {};
    },
    otherAmount() {
      const { shippingAmount, tradeInAmount, adhocAmount } = this.summary;
      return (shippingAmount || 0) + (tradeInAmount || 0) + (adhocAmount || 0);
    },
    hasManyRoles() {
      return Boolean(this.report && this.report.roles.length > 1);
    },
    rowFields() {
      const fields = [
        { key: 'billDate', label: 'Ngày', thStyle: thStyle('9%') },
        { key: 'billNumber', label: 'Hoá đơn', thStyle: thStyle('13%') },
        { key: 'kindName', label: 'Loại', thStyle: thStyle('10%') },
        {
          key: 'productName',
          label: 'Sản phẩm',
          thStyle: thStyle(this.hasManyRoles ? '30%' : '42%'),
        },
      ];
      if (this.hasManyRoles) {
        fields.push({
          key: 'jobTitleName',
          label: 'Vai trò',
          thStyle: thStyle('12%'),
        });
      }
      return fields.concat([
        {
          key: 'quantity',
          label: 'SL',
          thStyle: thStyle('5%'),
          tdClass: 'text-center',
        },
        {
          key: 'amount',
          label: 'Hoa hồng',
          thStyle: thStyle('12%'),
          tdClass: 'text-right',
        },
      ]);
    },
    kindOptions() {
      const sources = this.report ? this.report.sources : [];
      return [{ value: ALL_KINDS, text: 'Tất cả' }].concat(
        sources.map((source) => ({ value: source.key, text: source.name })),
      );
    },
    categoryOptions() {
      const categories = this.report ? this.report.categories : [];
      return [{ value: NO_FILTER, text: '-- Tất cả nhóm --' }].concat(
        categories.map((category) => ({
          value: category.id,
          text: category.name,
        })),
      );
    },
    visibleCategories() {
      const categories = this.report ? this.report.categories : [];
      return this.showAllCategories
        ? categories
        : categories.slice(0, TOP_CATEGORY_COUNT);
    },
    maxCategoryAmount() {
      const categories = this.report ? this.report.categories : [];
      return Math.max(1, ...categories.map((item) => Math.abs(item.amount)));
    },
    filteredRows() {
      if (!this.report) {
        return [];
      }
      const { keyword, categoryId, jobTitleId, kind } = this.tableFilter;
      const text = keyword.trim().toLowerCase();
      return this.report.rows.filter((row) => {
        if (kind !== ALL_KINDS && row.kind !== kind) {
          return false;
        }
        if (categoryId !== NO_FILTER && row.categoryId !== categoryId) {
          return false;
        }
        if (jobTitleId !== NO_FILTER && row.jobTitleId !== jobTitleId) {
          return false;
        }
        if (!text) {
          return true;
        }
        return (
          String(row.billNumber || '').includes(text) ||
          String(row.relatedBillNumber || '').includes(text) ||
          row.productName.toLowerCase().includes(text)
        );
      });
    },
    filteredTotal() {
      return this.filteredRows.reduce((sum, row) => sum + row.amount, 0);
    },
  },
  created() {
    // Tạo riêng cho component (methods bị Vue bind lại sẽ mất .clear())
    this.debounceKeyword = decounce(() => {
      this.tableFilter.keyword = this.keywordInput;
      this.currentPage = 1;
    }, TIME_TRIGGER);
    this.fetchData();
  },
  beforeDestroy() {
    this.debounceKeyword.clear();
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [{ title: 'Hoa hồng của tôi' }]);
  },
  methods: {
    selectMonth(monthsAgo) {
      const month = moment().subtract(monthsAgo, 'months');
      this.filter.fromDate = month.clone().startOf('month').format(DATE_INPUT_FORMAT);
      this.filter.toDate = (monthsAgo ? month.clone().endOf('month') : moment()).format(
        DATE_INPUT_FORMAT,
      );
      this.fetchData();
    },
    fetchData() {
      const error = this.validateDateRange(this.filter.fromDate, this.filter.toDate);
      if (error) {
        return makeToastFaile(error);
      }

      this.onLoading = true;
      ApiService.setHeader();
      ApiService.query('commission-reports/my', {
        params: {
          fromDate: this.toApiDate(this.filter.fromDate),
          toDate: this.toApiDate(this.filter.toDate),
        },
      })
        .then(({ data }) => {
          this.report = data.data || null;
          this.resetTableFilter();
        })
        .catch((err) => {
          this.report = null;
          makeToastFaile(this.getErrorMessage(err, 'Không tải được hoa hồng'));
        })
        .finally(() => {
          this.onLoading = false;
        });
    },
    resetTableFilter() {
      this.debounceKeyword.clear();
      this.tableFilter = emptyTableFilter();
      this.keywordInput = '';
      this.currentPage = 1;
      this.showAllCategories = false;
    },
    /** Bấm lại đúng mục đang lọc thì bỏ lọc */
    toggleFilter(field, value) {
      this.tableFilter[field] =
        this.tableFilter[field] === value ? NO_FILTER : value;
      this.currentPage = 1;
    },
    /** Số tiền kèm dấu: "+1.000", "-1.000", "0" (displayAmount tự thêm dấu âm) */
    withSign(value) {
      const amount = Number(value) || 0;
      return amount > 0 ? `+${this.displayAmount(amount)}` : this.displayAmount(amount);
    },
    amountTone(value) {
      const amount = Number(value) || 0;
      if (amount > 0) {
        return 'text-success';
      }
      return amount < 0 ? 'text-danger' : '';
    },
    barWidth(amount) {
      return `${Math.round((Math.abs(amount) / this.maxCategoryAmount) * 100)}%`;
    },
    kindVariant(kind) {
      return KIND_VARIANT[kind] || 'secondary';
    },
  },
};
</script>

<style scoped>
.form-label {
  display: block;
  font-size: 12px;
  color: #7e8299;
  margin-bottom: 4px;
}
.section-label {
  font-weight: 600;
  color: #181c32;
}
.stat-card {
  background: #f8f9fb;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  padding: 12px 16px;
  height: 100%;
}
.stat-card--primary {
  background: #f1faff;
  border-color: #cfe7fb;
}
.stat-card__label {
  font-size: 12px;
  color: #7e8299;
  margin-bottom: 6px;
}
.stat-card__value {
  font-size: 18px;
  font-weight: 700;
  color: #181c32;
}
.stat-card__value--lg {
  font-size: 22px;
}
.stat-card__note {
  font-size: 12px;
  color: #b5b5c3;
  margin-top: 4px;
}
.bar-row {
  width: 100%;
  border: 0;
  background: transparent;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
}
.bar-row:hover,
.bar-row:focus-visible {
  background: #f3f6f9;
  outline: none;
  box-shadow: 0 0 0 2px #cfe7fb;
}
.bar-row--active {
  background: #e1f0ff;
}
.bar-row__name {
  display: block;
  flex: 0 0 30%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #3f4254;
}
.bar-row__track {
  display: block;
  flex: 1 1 auto;
  height: 10px;
  background: #ebedf3;
  border-radius: 5px;
  overflow: hidden;
}
.bar-row__fill {
  display: block;
  height: 100%;
  background: #3699ff;
  border-radius: 5px;
}
.bar-row__fill--minus {
  background: #f64e60;
}
.bar-row__count {
  flex: 0 0 70px;
  text-align: right;
  color: #7e8299;
}
.bar-row__amount {
  flex: 0 0 110px;
  text-align: right;
  font-weight: 600;
}
@media (max-width: 767px) {
  .bar-row__name {
    flex-basis: 40%;
  }
  .bar-row__count {
    display: none;
  }
}
</style>
